/**
 * hiddenCategories.js — per-playlist persistent set of hidden category names.
 *
 * Mirrors VU Player Pro's "Hide Categories" feature (MobileHideCategoryActivity).
 * The user can mark categories they don't want to see in Live TV / Movies /
 * Series. We persist a JSON-encoded array under
 *   vupp_hidden_categories::<playlistId>::<contentType>
 *
 * Browse.jsx consults this set before rendering the sidebar so hidden
 * categories simply vanish (no items inside them either).
 *
 * Per-playlist + per-content-type so a user can hide "Adult" Live TV
 * categories on the family playlist but keep them visible on another.
 */

const ROOT = "vupp_hidden_categories";

function key(playlistId, contentType) {
  return `${ROOT}::${playlistId || "default"}::${contentType || "all"}`;
}

export function getHiddenCategories(playlistId, contentType) {
  try {
    const raw = localStorage.getItem(key(playlistId, contentType));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

export function setHiddenCategories(playlistId, contentType, set) {
  try {
    localStorage.setItem(
      key(playlistId, contentType),
      JSON.stringify(Array.from(set || []))
    );
  } catch {
    /* quota / privacy mode — fall through silently */
  }
}

export function toggleHiddenCategory(playlistId, contentType, category) {
  const set = getHiddenCategories(playlistId, contentType);
  if (set.has(category)) set.delete(category);
  else set.add(category);
  setHiddenCategories(playlistId, contentType, set);
  return set;
}

export function clearHiddenCategories(playlistId, contentType) {
  setHiddenCategories(playlistId, contentType, new Set());
}

export function isCategoryHidden(playlistId, contentType, category) {
  return getHiddenCategories(playlistId, contentType).has(category);
}
